import React, { useState } from "react";
import { Box, IconButton } from "@mui/joy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { theme } from "styles/theme";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";

// =========================================================================
// Task editor 공용 JSON / diff 코드 뷰.
// 코드 탭, 턴 변경 모달, 블록 JSON 모달이 모두 동일한 스타일을 쓰도록 한 곳에서 관리한다.
// =========================================================================

export type DiffLine = { kind: "same" | "add" | "del"; text: string };

// 라이브러리 회피용 line-level LCS.
export function computeLineDiff(
  beforeText: string,
  afterText: string
): DiffLine[] {
  const a = beforeText.split("\n");
  const b = afterText.split("\n");
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0)
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ kind: "del", text: a[i] });
      i++;
    } else {
      out.push({ kind: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ kind: "del", text: a[i++] });
  while (j < m) out.push({ kind: "add", text: b[j++] });
  return out;
}

// 키 정렬 우선순위 (스키마 읽기 순서). type 이 항상 최우선,
// 목록에 없는 키는 그 뒤에 알파벳 순. 원본/diff 가 같은 규칙을 쓴다.
const KEY_PRIORITY = [
  "type",
  // block
  "description",
  "action",
  // text
  "content",
  "typography_style",
  // text_input / field
  "fields",
  "label",
  "placeholder",
  "help_text",
  // button
  "allow_multiple_selection",
  "options",
  // option / branch
  "text",
  "is_correct",
  "show_from",
  "show_to",
  "restart_from",
  "hide_from",
  // reference
  "preset_text",
];

function keyRank(key: string): number {
  const idx = KEY_PRIORITY.indexOf(key);
  return idx === -1 ? KEY_PRIORITY.length : idx;
}

// 바이트 수를 사람이 읽기 좋은 단위로. (요약 표기에만 사용)
function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// data:image base64 는 미리보기/diff 에서 통째로 보여줄 필요가 없어
// "접두부… (크기)" 로 요약. 크기가 바뀌면 diff 가 감지하도록 디코딩 바이트를 남긴다.
function summarizeDataImage(value: string): string {
  const commaIdx = value.indexOf(",");
  if (commaIdx === -1) return value;
  const meta = value.slice(0, commaIdx + 1); // "data:image/png;base64,"
  const base64 = value.slice(commaIdx + 1);
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  const bytes = Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
  return `${meta}${base64.slice(0, 10)}… (${formatByteSize(bytes)})`;
}

// type 최우선 + 스키마 순서로 키를 정렬해 직렬화.
export function orderedStringify(value: unknown): string {
  return JSON.stringify(
    value,
    (_key, val) => {
      if (typeof val === "string" && val.startsWith("data:image/")) {
        return summarizeDataImage(val);
      }
      if (val && typeof val === "object" && !Array.isArray(val)) {
        const obj = val as Record<string, unknown>;
        const out: Record<string, unknown> = {};
        for (const k of Object.keys(obj).sort((a, b) => {
          const ra = keyRank(a);
          const rb = keyRank(b);
          return ra !== rb ? ra - rb : a.localeCompare(b);
        })) {
          out[k] = obj[k];
        }
        return out;
      }
      return val;
    },
    2
  );
}

// 두 값(보통 blocks)을 같은 정렬 규칙으로 직렬화 후 line diff.
export function diffOrderedJson(before: unknown, after: unknown): DiffLine[] {
  return computeLineDiff(orderedStringify(before), orderedStringify(after));
}

// JSON 구문 강조 색 — task editor 디자인 토큰 기반 (흰 배경).
const JSON_COLOR = {
  key: theme.vars.palette.Content.Highlight, // #0F55A7
  string: "#0F766E", // teal-700
  number: "#B45309", // amber-700
  keyword: theme.vars.palette.Content.HighlightWithAlert, // #F62754
  punctuation: theme.vars.palette.Content.Weak,
} as const;

// diff 라인 배경 틴트 (추가/삭제는 색 텍스트가 아닌 라인 배경으로).
const DIFF_BG = {
  add: "rgba(15, 118, 110, 0.10)",
  del: theme.vars.palette.Surface.SoftHighlightWithAlert, // #FFE3E9
} as const;

const JSON_TOKEN_REGEX =
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

// 박스 좌우 패딩 — CodeLine 의 full-bleed 배경 계산에 함께 쓰인다.
const PRE_PADDING_X = 20;

// 코드 박스 공용 스타일. 흰색 배경 + 둥근 테두리 + 넉넉한 여백 + 공용 스크롤바.
export const JSON_CODE_PRE_SX = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: theme.vars.palette.Surface.Forward,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  borderRadius: "8px",
  px: `${PRE_PADDING_X}px`,
  py: "18px",
  m: 0,
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "12px",
  lineHeight: 1.7,
  color: theme.vars.palette.Content.Strong,
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  tabSize: 2,
  scrollbarGutter: "stable",
  ...taskEditorScrollbarSx,
} as const;

// 라이브러리 없이 JSON 문자열을 색칠한 span 배열로 렌더.
function HighlightedJson({ json }: { json: string }) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  JSON_TOKEN_REGEX.lastIndex = 0;
  while ((match = JSON_TOKEN_REGEX.exec(json)) !== null) {
    if (match.index > lastIndex) {
      parts.push(json.slice(lastIndex, match.index));
    }
    const [, stringTok, colon, keyword, number] = match;
    if (stringTok !== undefined) {
      const isKey = colon !== undefined;
      parts.push(
        <span
          key={key++}
          style={{ color: isKey ? JSON_COLOR.key : JSON_COLOR.string }}
        >
          {stringTok}
        </span>
      );
      if (colon !== undefined) {
        parts.push(
          <span key={key++} style={{ color: JSON_COLOR.punctuation }}>
            {colon}
          </span>
        );
      }
    } else if (keyword !== undefined) {
      parts.push(
        <span key={key++} style={{ color: JSON_COLOR.keyword }}>
          {keyword}
        </span>
      );
    } else if (number !== undefined) {
      parts.push(
        <span key={key++} style={{ color: JSON_COLOR.number }}>
          {number}
        </span>
      );
    }
    lastIndex = JSON_TOKEN_REGEX.lastIndex;
  }
  if (lastIndex < json.length) {
    parts.push(json.slice(lastIndex));
  }
  return <>{parts}</>;
}

// JSON / diff 공용 라인. +/- 마커는 고정폭 거터에 두어, 원본/diff 모두
// 코드 시작 위치가 동일하게 정렬된다 (토글 시 좌측 여백이 안 밀림).
function CodeLine({ text, kind }: { text: string; kind: DiffLine["kind"] }) {
  const bg =
    kind === "add" ? DIFF_BG.add : kind === "del" ? DIFF_BG.del : "transparent";
  const marker = kind === "add" ? "+" : kind === "del" ? "-" : "";
  return (
    <Box
      sx={{
        backgroundColor: bg,
        // 박스 좌우 패딩을 상쇄해 배경 틴트를 박스 끝까지 full-bleed.
        mx: `-${PRE_PADDING_X}px`,
        px: `${PRE_PADDING_X}px`,
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: "2ch",
          color: JSON_COLOR.punctuation,
          userSelect: "none",
        }}
      >
        {marker}
      </span>
      <HighlightedJson json={text} />
    </Box>
  );
}

// 정렬된 JSON 문자열을 라인 단위로 렌더 (원본 보기).
export function JsonCodeLines({ json }: { json: string }) {
  return (
    <>
      {json.split("\n").map((line, idx) => (
        <CodeLine key={idx} text={line} kind="same" />
      ))}
    </>
  );
}

// diff 라인 렌더 (변경 보기).
export function DiffCodeLines({ lines }: { lines: DiffLine[] }) {
  return (
    <>
      {lines.map((line, idx) => (
        <CodeLine key={idx} text={line.text} kind={line.kind} />
      ))}
    </>
  );
}

// 정렬된 JSON 을 코드 박스로 렌더 + 우측 상단에 복사 버튼을 박스 안에 얹는다.
export function JsonCodeBox({
  json,
  copyable = true,
}: {
  json: string;
  copyable?: boolean;
}) {
  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}>
      <Box component="pre" sx={JSON_CODE_PRE_SX}>
        <JsonCodeLines json={json} />
      </Box>
      {copyable && (
        <JsonCopyButton
          text={json}
          sx={{ position: "absolute", top: "10px", right: "16px" }}
        />
      )}
    </Box>
  );
}

// 박스 안에 자연스럽게 얹는 복사 버튼. hover 는 아이콘(버튼) 영역에만 적용.
export function JsonCopyButton({
  text,
  sx,
}: {
  text: string;
  sx?: React.ComponentProps<typeof IconButton>["sx"];
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 권한 없으면 조용히 무시
    }
  };
  return (
    <IconButton
      size="sm"
      variant="plain"
      color="neutral"
      onClick={handleCopy}
      aria-label="JSON 복사"
      title={copied ? "복사됨!" : "JSON 복사"}
      sx={[
        {
          minWidth: "26px",
          minHeight: "26px",
          borderRadius: "6px",
          color: theme.vars.palette.Content.Weak,
          backgroundColor: theme.vars.palette.Surface.Forward,
          "&:hover": {
            color: theme.vars.palette.Content.Strong,
            backgroundColor: theme.vars.palette.Background.Level1,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {copied ? (
        <CheckRoundedIcon sx={{ fontSize: "16px" }} />
      ) : (
        <ContentCopyIcon sx={{ fontSize: "16px" }} />
      )}
    </IconButton>
  );
}
